import { useCallback, useRef, useState } from "react";
import { createElement } from "react";
import {
  fetchRitiriResi,
  giornoLungo,
  prenotaRitiroReso,
  STATI_ATTIVI,
  type EsitoRitiroUi,
  type Prenotazione,
} from "@/features/ritiri/api";
import { prettifyApiError } from "@/shared/api";
import {
  cancelReturn,
  creaRichiestaSostituzione,
  createReturn,
  fetchOrderReturns,
  fetchReturnPrefill,
  isActiveReturnConflict,
  type CreateReturnBody,
  type ReturnKind,
  type ReturnPrefill,
  type ReturnRecord,
  type RichiestaSostituzioneBody,
} from "./api";
import { ReturnDialog } from "./components/ReturnDialog";

/** Il giorno del ritiro dal cliente chiesto insieme alla creazione del reso. */
type RichiestaRitiro = { data: string };

export function useReturnDialog(
  /** ``ritiro`` si passa solo se il reso è nato col ritiro chiesto: i chiamanti
   *  esistenti (``toHaveBeenCalledWith(record)``) restano validi. */
  onCreated?: (record: ReturnRecord, ritiro?: Prenotazione | null) => void,
  /** Avviso tracking inviato dal dialog: la chat a schermo va ricaricata. */
  onMessaggioInviato?: () => void,
) {
  const [prefill, setPrefill] = useState<ReturnPrefill | null>(null);
  // Record creato: quando valorizzato il dialog resta aperto in stato «creato ✓»
  // con «Scarica PDF» (download coesistenza-safe). Prima il record veniva
  // scartato e il dialog chiuso subito → nessun accesso al PDF dalla chat.
  const [created, setCreated] = useState<ReturnRecord | null>(null);
  // Id del reso ATTUALMENTE a schermo, letto da ``prenotaRitiroSicuro`` quando
  // la sua promise risolve: un esito in ritardo per un reso che non è più
  // questo (chiuso/riaperto, oppure annulla-e-ricrea nato un ALTRO reso mentre
  // la prima prenotazione era ancora in volo) va scartato, non scritto sopra
  // lo stato del reso a schermo ora. Un ref, non uno stato: letto a risoluzione
  // avvenuta, mai per renderizzare.
  const currentReturnIdRef = useRef<number | null>(null);
  const [busyOrder, setBusyOrder] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Valvola anti-deadlock (gate idempotenza service.py): la create rifiuta un
  // secondo reso ATTIVO per ordine+kind. Quando scatta, ``conflict`` fa mostrare
  // al dialog «Annulla il precedente e ricrea»; ``pendingBody`` conserva il
  // payload rifiutato per ritentarlo dopo l'annullamento del reso attivo.
  const [conflict, setConflict] = useState(false);
  const pendingBody = useRef<CreateReturnBody | null>(null);
  const [ritiro, setRitiro] = useState<EsitoRitiroUi | null>(null);
  // Id del reso per cui una prenotazione (prima o riprova) è in volo, o
  // ``null``: guida sia il blocco di chiusura sia il busy del tasto «riprova».
  const [prenotazioneInCorsoPer, setPrenotazioneInCorsoPer] = useState<number | null>(null);

  // Etichetta creata e ritiro no: niente rollback, l'errore resta nel dialog
  // con il tasto per riprovare. Non solleva mai.
  const prenotaRitiroSicuro = useCallback(
    async (
      returnId: number,
      data: string,
      opts?: { daRiprova?: boolean },
    ): Promise<Prenotazione | null> => {
      setPrenotazioneInCorsoPer(returnId);
      try {
        const prenotazione = await prenotaRitiroReso(returnId, data);
        // Scartato: nel frattempo il dialog è passato a un altro reso (chiuso e
        // riaperto, o «annulla e ricrea» ne ha creato uno nuovo).
        if (currentReturnIdRef.current !== returnId) return null;
        setRitiro({ prenotazione, daRiprova: opts?.daRiprova });
        return prenotazione;
      } catch (e) {
        if (currentReturnIdRef.current !== returnId) return null;
        setRitiro({ errore: prettifyApiError(e) });
        return null;
      } finally {
        setPrenotazioneInCorsoPer((corrente) => (corrente === returnId ? null : corrente));
      }
    },
    [],
  );

  // Dopo la create: NON chiudere (stato «creato ✓» con «Scarica PDF»); col
  // ritiro chiesto prenota e passa l'esito a ``onCreated``.
  const concludi = useCallback(
    async (record: ReturnRecord, richiesta: RichiestaRitiro | null) => {
      setCreated(record);
      currentReturnIdRef.current = record.id;
      if (richiesta && record.kind === "reso") {
        // ``await`` in una riga a parte: dentro ``onCreated?.(...)`` l'optional
        // chaining corto-circuita l'INTERA chiamata quando ``onCreated`` manca
        // (caso reale: «annulla e ricrea» senza callback) — e con lei la
        // prenotazione, che deve partire comunque.
        const prenotazione = await prenotaRitiroSicuro(record.id, richiesta.data);
        onCreated?.(record, prenotazione);
      } else {
        onCreated?.(record);
      }
    },
    [onCreated, prenotaRitiroSicuro],
  );

  const openFor = useCallback(async (orderRef: string, kind: ReturnKind) => {
    setBusyOrder(orderRef);
    setSubmitError(null);
    setCreated(null);
    currentReturnIdRef.current = null;
    setConflict(false);
    setRitiro(null);
    try {
      setPrefill(await fetchReturnPrefill(orderRef, kind));
    } catch (e) {
      setSubmitError(prettifyApiError(e));
    } finally {
      setBusyOrder(null);
    }
  }, []);

  const onSubmit = useCallback(
    async (body: CreateReturnBody, richiesta?: RichiestaRitiro) => {
      setSubmitting(true);
      setSubmitError(null);
      setConflict(false);
      setRitiro(null);
      try {
        await concludi(await createReturn(body), richiesta ?? null);
      } catch (e) {
        // Gate idempotenza → offri la valvola invece di un vicolo cieco
        // (senza «Annulla» sulle righe la ricreazione resterebbe bloccata).
        pendingBody.current = body;
        setConflict(isActiveReturnConflict(e));
        setSubmitError(prettifyApiError(e));
      } finally {
        setSubmitting(false);
      }
    },
    [concludi],
  );

  // Passo 1 della sostituzione in due tempi: registra la richiesta, nessuna
  // chiamata SDA. Qui NON si attiva la valvola «annulla e ricrea»: quel ramo
  // ritenta una ``CreateReturnBody`` che questa strada non produce, e il tasto
  // resterebbe muto. In conflitto resta il messaggio, che dice già cosa fare.
  const saveRequest = useCallback(
    async (body: RichiestaSostituzioneBody) => {
      setSubmitting(true);
      setSubmitError(null);
      setConflict(false);
      try {
        const record = await creaRichiestaSostituzione(body);
        setCreated(record);
        currentReturnIdRef.current = record.id;
        onCreated?.(record);
      } catch (e) {
        setSubmitError(prettifyApiError(e));
      } finally {
        setSubmitting(false);
      }
    },
    [onCreated],
  );

  // Annulla il reso ATTIVO che ha fatto scattare il gate e ritenta la create,
  // con la scelta del ritiro VISIBILE ORA sulla striscia (mai quella del primo
  // invio: la striscia resta interattiva durante il conflitto). L'id non è
  // nell'errore del backend (porta solo num_rif_interno), quindi lo
  // recuperiamo dai returns dell'ordine filtrando kind + stato attivo.
  const cancelAndRecreate = useCallback(
    async (richiesta?: RichiestaRitiro) => {
      const body = pendingBody.current;
      if (!body) return;
      setSubmitting(true);
      setSubmitError(null);
      setConflict(false);
      try {
        const active = (await fetchOrderReturns(body.order_reference)).find(
          (r) => r.kind === body.kind && r.status !== "chiuso" && r.status !== "annullato",
        );
        if (!active) {
          throw new Error("Reso attivo non trovato: ricarica la pagina e riprova.");
        }
        // Il ritiro SDA non si annulla da qui (indice unico per return id, SDA
        // non lo cancella lato suo): se il reso che stiamo per annullare ha
        // ancora un ritiro ATTIVO, fermarsi PRIMA di cancellarlo, o quel
        // ritiro resta agganciato a un reso morto e se ne prenota un secondo
        // per quello nuovo — due corrieri per lo stesso cliente.
        const ritiriAttivo = await fetchRitiriResi([active.id]);
        const ritiroAttivo = ritiriAttivo[String(active.id)];
        if (ritiroAttivo && STATI_ATTIVI.includes(ritiroAttivo.stato)) {
          const dettaglio = ritiroAttivo.codice ? ` (codice ${ritiroAttivo.codice})` : "";
          throw new Error(
            `Il reso precedente ha il ritiro del ${giornoLungo(ritiroAttivo.data_ritiro)}${dettaglio}: ` +
              "annullalo dalla pagina Ritiri, voce Resi, prima di ricrearlo.",
          );
        }
        await cancelReturn(active.id);
        await concludi(await createReturn(body), richiesta ?? null);
      } catch (e) {
        setConflict(isActiveReturnConflict(e));
        setSubmitError(prettifyApiError(e));
      } finally {
        setSubmitting(false);
      }
    },
    [concludi],
  );

  const riprovaRitiro = useCallback(
    (data: string) => {
      // Guardia anche qui, non solo nel tasto disabilitato lato UI: un
      // secondo «Prenota il ritiro» mentre il primo è ancora in volo manda
      // una prenotazione in più, e SDA rifiuta la seconda con «già un ritiro
      // attivo» su un reso che in realtà l'ha appena ottenuto.
      if (created && prenotazioneInCorsoPer === null) {
        void prenotaRitiroSicuro(created.id, data, { daRiprova: true });
      }
    },
    [created, prenotazioneInCorsoPer, prenotaRitiroSicuro],
  );

  const close = useCallback(() => {
    setPrefill(null);
    setCreated(null);
    currentReturnIdRef.current = null;
    setConflict(false);
    setRitiro(null);
  }, []);

  // «In corso» solo se riguarda il reso ATTUALMENTE a schermo: un residuo
  // dell'id precedente (tra la risoluzione scartata e il prossimo render) non
  // deve bloccare la chiusura del reso nuovo.
  const ritiroInCorso = prenotazioneInCorsoPer !== null && prenotazioneInCorsoPer === created?.id;

  const dialog = prefill
    ? createElement(ReturnDialog, {
        prefill,
        created,
        submitting,
        submitError,
        conflict,
        ritiro,
        ritiroInCorso,
        onSubmit: (b: CreateReturnBody, r?: RichiestaRitiro) => void onSubmit(b, r),
        onSaveRequest: (b: RichiestaSostituzioneBody) => void saveRequest(b),
        onCancelAndRecreate: (r?: RichiestaRitiro) => void cancelAndRecreate(r),
        onRiprovaRitiro: riprovaRitiro,
        onMessaggioInviato,
        onClose: close,
      })
    : null;

  return { openFor, dialog, busyOrder, openError: submitError };
}
